import * as L from 'leaflet';
import 'leaflet-draw';
import { Component, Injector, ViewChild } from '@angular/core';

import {
  BgsLeafletComponent,
  BgsLeafletModule,
  BoundingBox,
  DrawBBoxControl,
  LegendControl,
  MapLayer,
  PagedLegendDisplay,
} from 'src/app/bgsLeaflet/public_api';


import { EVSidebarService } from '../ev-sidebar/ev-sidebar.service';
import { SidebarMode } from '../ev-sidebar/ev-sidebarMode.enum';
import { UserNotificationService } from '../notification/userNotifications.service';
import { EvLayerClickManager } from './ev-LayerClickManager';
import { EVMapService } from './ev-map.service';

@Component({
  selector: 'app-ev-map',
  templateUrl: './ev-map.component.html',
  styleUrls: ['./ev-map.component.scss'],
  standalone: true,
  imports: [BgsLeafletModule],
})
export class EvMapComponent {
  @ViewChild('mapComponent', { static: true }) bgsLeaflet: BgsLeafletComponent;

  public currentBaseLayers = new Array<MapLayer>();
  public currentMapLayers = new Array<MapLayer>();
  public placingPin = false;
  private bboxControl = new DrawBBoxControl(false);

  constructor(
    private readonly sidebarService: EVSidebarService,
    private readonly mapService: EVMapService,
    private readonly injector: Injector,
    private readonly notificationService: UserNotificationService,
  ) {
    mapService.watchMapLayers().subscribe((mapLayers: Array<MapLayer>) => {
      if (null != this.bgsLeaflet) {
        this.setMapLayers(mapLayers);
      }
    });
    mapService.watchBaseLayers().subscribe((mapLayers: Array<MapLayer>) => {
      this.setBaseLayers(mapLayers);
    });
    mapService.watchPlacingPin().subscribe((placingPin: boolean) => {
      this.placingPin = placingPin;
    });

    mapService.watchStartDrawBoundingBox().subscribe(() => {
      this.bboxControl.startDraw();
    });
    mapService.watchClearBoundingBox().subscribe(() => {
      this.bboxControl.setBoundingBox(BoundingBox.makeUnbounded());
    });
    mapService.watchZoomToLocation().subscribe((location: L.LatLng) => {
      if (location) {
        this.bgsLeaflet.leafletMapObj.flyTo(location, 12);
      }
    });
    this.sidebarService.sidepanelModeChange().subscribe((mode: SidebarMode) => {
      // console.debug('SidebarMode', mode);
      // NOT SUPPORTED YET
      this.bboxControl.hideLayer(mode !== SidebarMode.FILTER);
    });
  }

  public leafletMapObjReady(): void {
    const basemapLayerOSM = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    });
    basemapLayerOSM.addTo(this.bgsLeaflet.leafletMapObj);
    this.bgsLeaflet
      .addControl(L.control.scale().setPosition('bottomright'))
      .addControl(new LegendControl().setDisplay(new PagedLegendDisplay()).setPosition('bottomright'))
      // .addControl(new LayerControl(false).setPosition('bottomright'))
      .addControl(this.bboxControl)
      .addControl(L.control.zoom().setPosition('bottomleft'))
      // .addControl(new SearchControl(true).setPosition('topleft'))
      .enableLayerClickManager(new EvLayerClickManager(this.injector));

    this.bboxControl.watchBoundingBox().subscribe((newBounds: BoundingBox) => {
      this.mapService.setBoundingBox(newBounds);
    });

    this.setBaseLayers(this.mapService.baseLayers);
    setTimeout(() => {
      this.setMapLayers(this.mapService.mapLayers);
    }, 100);

    // TODO: remove this once the maintenance period has passed
    const date = new Date();
    const start = new Date('2022-09-27');
    const end = new Date('2022-10-06');
    if (date >= start && date <= end) {
      this.notificationService.sendWarning(
        'Please be aware that due to scheduled maintenance there will be temporary disruption to certain features on this web application between 17:00 and 19:00 on Wednesday 5th October 2022. In case of unanticipated issues, the application should be considered at risk for the rest of the evening. Thank you for your understanding and we apologise for the inconvenience.',
        99999,
      );
    }
  }

  private setBaseLayers(mapLayers: Array<MapLayer>): void {
    if (null != this.bgsLeaflet) {
      this.currentBaseLayers.forEach((layer: MapLayer) => {
        this.bgsLeaflet.removeLayerById(layer.id);
      });
      this.bgsLeaflet.addLayers(mapLayers);

      this.currentBaseLayers = mapLayers.slice();
    }
  }

  private setMapLayers(mapLayers: Array<MapLayer>): void {
    if (null != this.bgsLeaflet) {
      // remove layers no longer there
      this.currentMapLayers.forEach((layer: MapLayer) => {
        if (!mapLayers.includes(layer)) {
          this.bgsLeaflet.removeLayerById(layer.id);
        }
      });

      // only add layers that weren't there previously
      this.bgsLeaflet.addLayers(mapLayers.filter((layer) => !this.currentMapLayers.includes(layer)));

      this.currentMapLayers = mapLayers.slice();
    }
  }
}
